import { FC, useMemo } from "react";
import { Autocomplete } from "@material-ui/lab";
import { TextField } from "@material-ui/core";
import { IconPreview, ProcessedIcon, useIcons } from "src/contexts/IconContext";

export type FontAwesomeIconOption = {
  key: string;
  label: string;
  processedIcon: ProcessedIcon; // Changed from IconProp to ProcessedIcon
  value: string;  // 'solid:circle' or similar, for matching
};

type FontAwesomeIconSelectorProps = {
  value: string; // e.g., 'solid:circle'
  onChange: (selected: FontAwesomeIconOption) => void;
  label?: string;
};

const FontAwesomeIconSelector: FC<FontAwesomeIconSelectorProps> = ({
  value,
  onChange,
  label = "Icon",
}) => {
  const { icons, isLoading, error } = useIcons();

  // Helper function to normalize unicode value (remove 0x prefix if present)
  const normalizeUnicode = (val: string): string => {
    return val.startsWith('0x') ? val.slice(2) : val;
  };

  // Helper function to find icon by unicode value
  const findIconByUnicode = (unicodeValue: string): FontAwesomeIconOption | null => {
    const normalizedUnicode = normalizeUnicode(unicodeValue);
    return flatOptions.find(opt => opt.processedIcon.unicode === normalizedUnicode) || null;
  };

  // Helper function to check if value is a unicode (hex) value
  const isUnicodeValue = (val: string): boolean => {
    const normalized = normalizeUnicode(val);
    return /^[0-9a-fA-F]+$/.test(normalized) && !val.includes(':');
  };

  // Convert ProcessedIcon[] to grouped FontAwesomeIconOption[]
  const iconOptions = useMemo(() => {
    if (!icons.length) return [];
    
    // Group icons by style/type
    const groupedIcons: Record<string, FontAwesomeIconOption[]> = {};
    
    icons.forEach((processedIcon) => {
      const option: FontAwesomeIconOption = {
        key: processedIcon.value,
        label: processedIcon.label, // Remove style from label since it's now grouped
        processedIcon: processedIcon,
        value: processedIcon.value,
      };
      
      const groupKey = processedIcon.style.charAt(0).toUpperCase() + processedIcon.style.slice(1);
      
      if (!groupedIcons[groupKey]) {
        groupedIcons[groupKey] = [];
      }
      groupedIcons[groupKey].push(option);
    });
    
    // Sort each group by label
    Object.values(groupedIcons).forEach(group => {
      group.sort((a, b) => a.label.localeCompare(b.label));
    });
    
    return groupedIcons;
  }, [icons]);
  
  // Flatten for autocomplete options
  const flatOptions = useMemo(() => {
    return Object.values(iconOptions).flat();
  }, [iconOptions]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    
    // If value is a unicode value (like 0xf029), find by unicode
    if (isUnicodeValue(value)) {
      return findIconByUnicode(value);
    }
    
    // Otherwise, find by the style:iconName format
    const normalizedValue = normalizeUnicode(value);
    return flatOptions.find((opt) => opt.value === normalizedValue) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, flatOptions]);

  if (isLoading) {
    return (
      <TextField
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        disabled
        value="Loading icons..."
      />
    );
  }

  if (error) {
    return (
      <TextField
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        error
        helperText={`Error loading icons: ${error}`}
        value=""
      />
    );
  }

  return (
    <Autocomplete<FontAwesomeIconOption, false, true, false>
      value={selectedOption}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue === 'object') {
          onChange(newValue);
        }
      }}
      options={flatOptions}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => option.processedIcon.style.charAt(0).toUpperCase() + option.processedIcon.style.slice(1)}
      size="small"
      fullWidth
      disableClearable={true}
      loading={isLoading}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="outlined" />
      )}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconPreview 
            icon={option.processedIcon} 
            size={16}
            className="icon-option-preview"
          />
          <span>{option.label}</span>
        </li>
      )}
      getOptionSelected={(option, value) => option.value === value.value}
    />
  );
};

export default FontAwesomeIconSelector;